import { IResponse, consumer } from '@bemap/utils';
import type { CreateLeadActionDto, FunnelStep } from '@bemap/crm-microfrontends';

export type {
  SalesFunnel,
  LeadWithFunnel,
  LeadAction,
  CampaignMetrics,
  CreateLeadActionDto,
  FunnelStep,
} from '@bemap/crm-microfrontends';

export interface CreateSalesFunnelDto {
  name: string;
  steps: FunnelStep[];
  is_default?: boolean;
}

export interface UpdateSalesFunnelDto {
  name?: string;
  steps?: FunnelStep[];
  is_default?: boolean;
}

export interface UpdateLeadFunnelDto {
  funnel_id: number;
  step_id: string;
}

export const getFunnels = async (): Promise<IResponse> => {
  return consumer('GET', '/crm/funnels');
};

export const getFunnel = async (id: number): Promise<IResponse> => {
  return consumer('GET', `/crm/funnels/${id}`);
};

export const createFunnel = async (payload: CreateSalesFunnelDto): Promise<IResponse> => {
  return consumer('POST', '/crm/funnels', payload);
};

export const updateFunnel = async (id: number, payload: UpdateSalesFunnelDto): Promise<IResponse> => {
  return consumer('PATCH', `/crm/funnels/${id}`, payload);
};

export const deleteFunnel = async (id: number): Promise<IResponse> => {
  return consumer('DELETE', `/crm/funnels/${id}`);
};

export const getCrmLeads = async (funnelId?: number, stepId?: string, view?: string): Promise<IResponse> => {
  const params = new URLSearchParams();
  if (funnelId) params.append('funnelId', funnelId.toString());
  if (stepId) params.append('stepId', stepId);
  if (view) params.append('view', view);
  
  const query = params.toString();
  return consumer('GET', `/crm/leads${query ? `?${query}` : ''}`);
};

export const moveLeadToStep = async (leadId: number, payload: UpdateLeadFunnelDto): Promise<IResponse> => {
  return consumer('PATCH', `/crm/leads/${leadId}/funnel-step`, payload);
};

export const getLeadActions = async (leadId: number): Promise<IResponse> => {
  return consumer('GET', `/crm/leads/${leadId}/actions`);
};

export const createLeadAction = async (leadId: number, payload: CreateLeadActionDto): Promise<IResponse> => {
  return consumer('POST', `/crm/leads/${leadId}/actions`, payload);
};

export const updateLeadAction = async (leadId: number, actionId: number, payload: Partial<CreateLeadActionDto> & { completed?: boolean }): Promise<IResponse> => {
  return consumer('PATCH', `/crm/leads/${leadId}/actions/${actionId}`, payload);
};

export const getUpcomingReminders = async (limit?: number): Promise<IResponse> => {
  const params = limit ? `?limit=${limit}` : '';
  return consumer('GET', `/crm/reminders${params}`);
};

export const getCampaignMetrics = async (campaignId: number): Promise<IResponse> => {
  return consumer('GET', `/crm/campaigns/${campaignId}/metrics`);
};

export const getOverviewMetrics = async (startDate?: string, endDate?: string): Promise<IResponse> => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const query = params.toString();
  return consumer('GET', `/crm/metrics/overview${query ? `?${query}` : ''}`);
};

export default {
  getFunnels,
  getFunnel,
  createFunnel,
  updateFunnel,
  deleteFunnel,
  getCrmLeads,
  moveLeadToStep,
  getLeadActions,
  createLeadAction,
  updateLeadAction,
  getUpcomingReminders,
  getCampaignMetrics,
  getOverviewMetrics,
};

